/**
 * JourneysHome — the Booking Journeys screen (route `journeys`), Operations
 * Admin rail. Ported from the source prototype's Journeys rail (Booking
 * journeys) — the list, the New-journey creator, the Branding tab and the full
 * editor/preview. The presentational pieces (OpsJourneysTab / JourneyCreator /
 * TenantBrandingPanel / JourneyEditor + panels + renderer) are the source's,
 * transcribed verbatim; THIS container owns data through the typed
 * @v0y4ge/api-client journeys ops (declared, REQ-063; preview-seeded until they
 * land — hence the amber DataSourceStrip). Tenant identity is RUNTIME context
 * (useTenant → whoami), never a baked constant (CC GM14 §4 item 1). NOTE: the
 * preview seed currently serves bank-world journey records for every tenant —
 * a flagged backend gap (per-tenant scoping lands with the /v1/journeys
 * contract-registration wave). WRITES await their ops (no fake interactivity).
 */
import { useEffect, useRef, useState } from "react";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { useNav } from "../../shell/router";
import { useWfResource, useBusy } from "../workforce/useWorkforce";
import { WfPending } from "../workforce/wf-shared";
import { toast } from "../../shell/toast";
import { T, Chip } from "@v0y4ge/jrni-ui";
import { ensureJourneysCss } from "./journeys-styles";
import { setActiveBrand, setJourneysBaseURL, type Journey } from "./journey-model";
import { OpsJourneysTab, TenantBrandingPanel } from "./journey-list";
import JourneyEditor from "./JourneyEditor";
import { useTenant } from "../../shell/access";
import { useT } from "@v0y4ge/i18n-runtime";
import {
  listJourneys, createJourney, patchAdminJourney, deleteJourney, duplicateJourney,
  setJourneyStatus, getTenantBrand, setTenantBrand, type TenantBrand,
} from "./journeys-api";

export default function JourneysHome() {
  const t = useT();
  const nav = useNav();
  ensureJourneysCss();
  const tenant = useTenant();
  const slug = tenant?.slug ?? "";
  const { data, loading, error, reload } = useWfResource<{ journeys: Journey[] }>("journeys|" + slug, () => listJourneys(slug));
  const brandRes = useWfResource<TenantBrand>("journeys|brand|" + slug, () => getTenantBrand(slug));
  const { run } = useBusy();
  const [tab, setTab] = useState<"journeys" | "branding">("journeys");
  // URL is the source of truth for editor selection (deep-link/refresh/back-safe),
  // mirroring StaffEditorPage's nav.params.id pattern.
  const editingId = (nav.params?.id as string | undefined) ?? null;
  const setEditingId = (id: string | null) => nav.go("journeys", id ? { id } : {});
  // Local working copy so live editing + preview are instant; persisted to the op.
  const [working, setWorking] = useState<Journey[]>([]);
  useEffect(() => { if (data?.journeys) setWorking(data.journeys); }, [data]);
  useEffect(() => { if (brandRes.data) { setActiveBrand(brandRes.data as unknown as Record<string, string>); setJourneysBaseURL(brandRes.data.domain); } }, [brandRes.data]);

  const journeys = working;
  const warnedNoWrite = useRef(false);

  /**
   * ⛔ THIS EDITOR NO LONGER WRITES (drop 1, 2026-09-05).
   *
   * It used to PATCH the whole journey object on a 500ms debounce and swallow the
   * refusal (`.catch(() => {})`), so every edit looked saved and none was. The
   * replacement write path is the All Journeys screen's Appointments tab, which
   * patches whitelisted fields only. Until an editor edit has a contract-shaped
   * home, edits here stay local AND SAY SO — a control that appears to write and
   * does not is the thing the rule exists to prevent.
   */
  const onEditorChange = (nj: Journey) => {
    setWorking((js) => js.map((x) => (x.id === nj.id ? nj : x)));
    if (!warnedNoWrite.current) {
      warnedNoWrite.current = true;
      toast({
        tone: "warning",
        title: "Not saved",
        subtitle: "This editor's changes stay on screen only. Page settings save from All Journeys.",
      });
    }
  };
  const create = (j: Journey) => run("create", async () => {
    const created = await createJourney({ templateId: j.templateId, name: j.name, type: j.type, flowOrder: j.flowOrder, scan: j._scan || null });
    toast({ tone: "success", title: "Journey created", subtitle: `${created.name} · draft` });
    await reload();
    setEditingId(created.id);
  });
  const duplicate = (id: string) => run(`dup:${id}`, async () => {
    const copy = await duplicateJourney(id);
    toast({ tone: "success", title: "Journey duplicated", subtitle: `${copy.name} · draft` });
    reload();
  });
  const remove = (id: string) => run(`del:${id}`, async () => {
    const j = journeys.find((x) => x.id === id);
    await deleteJourney(id);
    toast({ tone: "success", title: "Journey deleted", subtitle: (j ? j.name : "Journey") + " removed" });
    reload();
  });
  const update = (nj: Journey) => run(`upd:${nj.id}`, async () => {
    // The only list-level write the contract accepts from this screen is the status op.
    if (nj.status === "draft") await setJourneyStatus(nj.id, "draft");
    else await setJourneyStatus(nj.id, "published");
    setWorking((js) => js.map((x) => (x.id === nj.id ? nj : x)));
  });
  const setAllInherit = () => run("inherit-all", async () => {
    const targets = journeys.filter((j) => !j.theme.inheritBrand);
    try {
      await Promise.all(targets.map((j) => patchAdminJourney(j.id, { theme: { ...j.theme, inheritBrand: true } })));
    } catch (err) {
      toast({ tone: "danger", title: "Couldn't apply the master brand", subtitle: String((err as Error)?.message || err) });
      return;
    }
    toast({ tone: "success", title: "All journeys now inherit the master brand" });
    reload();
  });
  const saveBrand = (brand: TenantBrand) => run("brand-save", async () => { await setTenantBrand(slug, brand); setActiveBrand(brand as unknown as Record<string, string>); reload(); });
  const resetBrand = () => run("brand-reset", async () => { const b = await getTenantBrand(slug); brandRes.reload(); setActiveBrand(b as unknown as Record<string, string>); });

  const editing = journeys.find((x) => x.id === editingId);
  const tLabel = tenant?.name ?? slug;

  return (
    <Screen rail="journeys">
      <div className="jrn-scope" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
        {editing ? (
          <JourneyEditor journey={editing} onChange={onEditorChange} onBack={() => setEditingId(null)} />
        ) : (
          <>
            <PageHeader title={t('journeys.home.title5')} breadcrumb="Estate configuration · All stores"
              subtitle={t('journeys.home.subtitle')} />
            <DataSourceStrip screen="journeys" ops={["listJourneys", "getTenantBrand", "createJourney", "updateAdminJourney", "deleteJourney", "duplicateJourney", "setJourneyStatus", "setTenantBrand"]} />
            <div style={{ padding: "14px 24px 48px", display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="row gap-2">
                {([["journeys", "Journeys"], ["branding", "Branding"]] as const).map(([id, l]) => (
                  <Chip key={id} selected={tab === id} onClick={() => setTab(id)} style={{ height: 32, fontSize: 12.5 }}>{l}</Chip>
                ))}
              </div>
              {error ? <WfPending screen="journeys" opName="listJourneys" error={error} onRetry={reload} />
                : loading && !journeys.length ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t('journeys.home.text')}</div>
                : tab === "branding" ? (
                  <TenantBrandingPanel tenant={slug} label={tLabel}
                    journeys={journeys}
                    initialBrand={brandRes.data}
                    onSaveBrand={saveBrand} onResetBrand={resetBrand}
                    onAllInherit={setAllInherit} onOpenJourney={(id: string) => setEditingId(id)} />
                ) : (
                  <OpsJourneysTab journeys={journeys} tenantLabel={tLabel}
                    onOpen={setEditingId} onDuplicate={duplicate} onDelete={remove}
                    onUpdate={update} onCreate={create} />
                )}
            </div>
          </>
        )}
      </div>
    </Screen>
  );
}
